// EVA-279: the typed-decimal rules the Discounting & Purchasers' Costs tab uses.
//
// Deliberately NOT numberFormat.js. That module reads numbers the way a
// spreadsheet wrote them, because grid cells are pasted out of Excel: "1.234,56"
// and "99 200" are both numbers there. These inputs are typed by hand, and the
// spec is strict about what a keystroke may produce — a percentage accepts no
// commas at all, and a Fixed Amount accepts them only as grouping.
//
// EVA-298: despite the jsx/ path this module has a non-React consumer,
// controllers/rounding_policy_controller.js. Keep it dependency-free — there is
// no ESLint here, so a React import added on the assumption that every consumer
// is a component would break that controller with nothing to catch it.

// Percentage drafts: digits and at most one decimal point, at most two decimals.
// No sign, exponent, whitespace, comma or literal "%".
const DECIMAL_DRAFT = /^\d*(\.\d{0,2})?$/

// Fixed Amount drafts additionally allow commas anywhere, so grouping survives
// mid-edit ("12,50" is accepted and canonicalised on blur).
const FIXED_AMOUNT_DRAFT = /^[\d,]*(\.\d{0,2})?$/

export const DECIMAL_PLACES = 2

export function isValidDecimalDraft(draft) {
  return DECIMAL_DRAFT.test(String(draft ?? ''))
}

export function isValidFixedAmountDraft(draft) {
  return FIXED_AMOUNT_DRAFT.test(String(draft ?? ''))
}

// Blank and a lone "." are null, not zero: the tab must distinguish "not entered"
// from "entered as 0", and so must the column behind it.
export function parseDecimalInput(draft) {
  const raw = String(draft ?? '').trim()
  if (raw === '' || raw === '.') return null

  const n = Number(raw)
  return Number.isFinite(n) ? n : null
}

export function parseFixedAmountInput(draft) {
  return parseDecimalInput(String(draft ?? '').replace(/,/g, ''))
}

// Unfocused display: exactly two decimals, ungrouped. An explicit 0 shows
// "0.00"; blank stays blank.
export function formatDecimalInput(value) {
  if (value === null || value === undefined || value === '') return ''

  const n = Number(value)
  return Number.isFinite(n) ? n.toFixed(DECIMAL_PLACES) : ''
}

// Unfocused display for Fixed Amount: en-GB grouping, exactly two decimals, no
// currency symbol. Commas are presentation only — state stays numeric.
export function formatFixedAmountInput(value) {
  if (value === null || value === undefined || value === '') return ''

  const n = Number(value)
  if (!Number.isFinite(n)) return ''

  return new Intl.NumberFormat('en-GB', {
    minimumFractionDigits: DECIMAL_PLACES,
    maximumFractionDigits: DECIMAL_PLACES
  }).format(n)
}

// A basis point is 1/100 of a percentage point, so 25 bps moves the rate by 0.25.
// Returns null when there is nothing to adjust, which is what disables the
// buttons — the existing rate input stays authoritative.
export function adjustByBasisPoints(value, basisPoints) {
  const n = parseDecimalInput(value)
  if (n === null) return null

  const moved = n + basisPoints / 100
  // Adjustments are cumulative, so the result is re-rounded to the same two
  // decimals the input accepts; otherwise 8.55 + 0.05 drifts into 8.600000001.
  const rounded = Math.round(moved * 100) / 100

  return rounded < 0 ? null : rounded.toFixed(DECIMAL_PLACES)
}
